import { CHANNELS, toChannelId, type ChannelId, type ChannelSubset } from './registry';

/**
 * Which measures a channel's steps can record at all: its delivery states and
 * actions, plus the counters derived from them. A port of Ember's
 * `deliveryStatesAndActionsPerType` and the email additions `actionInfo` makes
 * (`app/utils/action-info.ts`).
 */

/**
 * Channels that record conversions only for an automation with a conversion
 * goal: Ember's `optionallyConverts`, which only the webhook action sets.
 */
const GOAL_GATED_CONVERSION_CHANNELS: readonly ChannelSubset<'webhook'>[] = ['webhook'];

/**
 * The counters email alone carries, keyed by the total they split. The machine
 * remainders are this vocabulary's names for Ember's `total_minus_human_*`.
 */
const EMAIL_SPLIT_COUNTERS: ReadonlyMap<string, readonly string[]> = new Map([
  ['opened', ['human_opened', 'machine_opened', 'prefetch_opened']],
  ['clicked', ['human_clicked', 'machine_clicked']],
]);

function supportedMeasures(id: ChannelId, deliveryLists: readonly string[]): ReadonlySet<string> {
  const measures = new Set(deliveryLists);

  // A click-to-open rate needs both of the counts it relates.
  if (measures.has('clicked') && measures.has('opened')) {
    measures.add('clicked_to_opened');
  }

  if (id === 'email') {
    for (const [total, parts] of EMAIL_SPLIT_COUNTERS) {
      if (measures.has(total)) {
        parts.forEach((part) => measures.add(part));
      }
    }
    // Recipients who never consented to open tracking, which only an open
    // pixel can have (AUD-4189).
    measures.add('untracked');
  }

  return measures;
}

const SUPPORTED_MEASURES: ReadonlyMap<ChannelId, ReadonlySet<string>> = new Map(
  CHANNELS.map(({ id, deliveryStates, deliveryActions }) => [
    id,
    supportedMeasures(id, [...deliveryStates, ...deliveryActions]),
  ]),
);

/**
 * Whether a channel's steps can record the measure. `canConvert` is whether
 * the automation has a conversion goal, which decides it for a webhook step
 * alone; a reader with no automation in hand passes false, as Ember's cards
 * call `actionInfo` without one.
 */
export function isMeasureSupported(id: ChannelId, measure: string, { canConvert }: { canConvert: boolean }): boolean {
  if (measure === 'converted' && GOAL_GATED_CONVERSION_CHANNELS.some((channel) => channel === id)) {
    return canConvert;
  }
  return SUPPORTED_MEASURES.get(id)?.has(measure) ?? false;
}

/** {@link isMeasureSupported} for a string that may name a channel; one that doesn't supports no measure. */
export function isMeasureSupportedForValue(value: string, measure: string, options: { canConvert: boolean }): boolean {
  const id = toChannelId(value);
  return id !== null && isMeasureSupported(id, measure, options);
}
